<template>
  <div class="coord-dashboard-layout" :style="wrapperStyle" onselectstart="return false">
    <!-- no data -->
    <div :class="$style['empty-dashboard']" v-if="isEmptyDashboardTree">
      <div class="no_map_box" style="margin-left: -24px; margin-top: -24px;">
        <p class="no_map_title"><label>데이터가 없습니다.</label></p>
        <p class="no_map_desc"><label>대시보드를 추가해주십시오.</label></p>
      </div>
    </div>

    <!-- 컨트롤바 -->
    <w-dashboard-controlbar :dashboardScale.sync="dashboardScale"></w-dashboard-controlbar>

    <!-- 슬라이드쇼 컨트롤바 -->
    <w-slideshow-controlbar></w-slideshow-controlbar>

    <!-- 편집 영역 선택 -->
    <w-drag-select :disabled="spaceDown"></w-drag-select>

    <!-- 캔버스 영역 -->
    <div :style="eventTargetStyle" class="event-target">
      <drop
        ref="dashboardLayout"
        :class="[$style['dashboard-layout'], 'event-target']"
        :style="dashboardLayoutStyle"
        @drop="onDropNode"
        >
        <!-- 애니메이션, 그라데이션 배경 -->
        <w-particle-background :options="dashboardData.aniOptions" v-if="backgroundType === DashboardConst.BACKGROUND_TYPE.ANIMATION"></w-particle-background>
        <w-gradient-background :options="dashboardData.aniOptions" v-else-if="backgroundType === DashboardConst.BACKGROUND_TYPE.GRADIENT"></w-gradient-background>
        <transition-group name="el-fade-in-linear">
          <vue-drag-resize
            v-for="(widget, index) in widgetLayout"
            ref="dragResize"
            :style="widgetStyle"
            :key="widget.i"
            :x="widget.x"
            :y="widget.y"
            :w="widget.w"
            :h="widget.h"
            :z="widget.z"
            :minw="DashboardConst.COORD_LAYOUT_OPTION.MIN_WIDGET_W"
            :minh="DashboardConst.COORD_LAYOUT_OPTION.MIN_WIDGET_H"
            :preventActiveBehavior="!isEditMode"
            :parentLimitation="true"
            :parentScaleX="isFullScale ? dashboardScaleX : dashboardScale"
            :parentScaleY="isFullScale ? dashboardScaleY : dashboardScale"
            :parentW="dashboardWidth"
            :parentH="dashboardHeight"
            :isActive="widget.active"
            :isDraggable="isEditMode"
            :isResizable="isEditMode"
            :aspectRatio="shiftDown"
            @hook:mounted="onWidgetMounted(index)"
            @mousedown.native="onWidgetMouseDown($event, widget.i)"
            @mouseup.native="onWidgetMouseUp($event)"
            @dragging="onWidgetDragging($event, widget.i)"
            @dragstop="onWidgetDragged($event, widget.i)"
            @resizestop="onWidgetResized($event, widget.i)"
            v-show="widgetVisible(widget)">
              <component :is="widgetView(widget)" :layout="widget"/>
          </vue-drag-resize>
        </transition-group>
      </drop>
    </div>
    <w-refresh-timer v-if="isMonitoringMode" @refresh="widgetLoadData" type="DASHBOARD"></w-refresh-timer>
  </div>
</template>

<script>
import Vue from 'vue';
import VueDragResize from 'vue-drag-resize';
import { Drop } from 'vue-drag-drop';
import html2canvas from 'html2canvas';
import axios from 'axios';

import { isEmpty } from '@/utils/basic';
import msgUtil from '@/utils/message';

import { find, filter, forEach, maxBy, cloneDeep, map, has, assign, isEmpty as isEmpty2 } from 'lodash';

import WidgetAPI from '@/api/dashboard/widget';

import DashboardConst from '@/constants/dashboard';
import Layout from '@/mixins/dashboard/layout';

import editHistory from '@/views/dashboard/js/edit-history';
import glowKeyframe from '@/views/dashboard/js/glow-keyframe';
import '@/views/dashboard/js/highcharts-ex';

import WSlideshowControlbar from '@/views/dashboard/components/SlideshowControlbar';
import WDashboardControlbar from '@/views/dashboard/components/DashboardControlbar';
import WParticleBackground from '@/views/dashboard/components/frame/ParticleBackground';
import WGradientBackground from '@/views/dashboard/components/frame/GradientBackground';
import WDragSelect from '@/views/dashboard/components/DragSelect';
import WRefreshTimer from '@/views/dashboard/components/RefreshTimer';

Vue.component('vue-drag-resize', VueDragResize);

export default {
  name: 'WCoordDashboardLayout',
  components: {
    VueDragResize,
    Drop,
    WSlideshowControlbar,
    WDashboardControlbar,
    WParticleBackground,
    WGradientBackground,
    WDragSelect,
    WRefreshTimer,
  },
  mixins: [Layout],
  provide() {
    const layoutData = {};
    Object.defineProperty(layoutData, 'dashboardScaleX', {
      enumerable: true,
      get: () => this.dashboardScaleX,
    });
    Object.defineProperty(layoutData, 'dashboardScaleY', {
      enumerable: true,
      get: () => this.dashboardScaleY,
    });
    Object.defineProperty(layoutData, 'dashboardScale', {
      enumerable: true,
      get: () => this.dashboardScale,
    });
    Object.defineProperty(layoutData, 'isFullScale', {
      enumerable: true,
      get: () => this.isFullScale,
    });
    return {
      layoutData,
    };
  },
  data() {
    return {
      dashboardScaleX: 1,
      dashboardScaleY: 1,
      dashboardScale: 1,
      mouseDown: false,
      mouseMove: false,
      ctrlDown: false,
      spaceDown: false,
      shiftDown: false,
      copiedWidgets: [],
      preventWidgetEvent: false,
      draggingWidget: {},
      widgetLoadDataSource: null,
      previousScrollPosition: {
        top: null,
        left: null,
      },
    };
  },
  computed: {
    wrapperStyle() {
      return {
        cursor: this.mouseMove ? 'grabbing' : 'default',
        overflow: this.isFullScale ? 'hidden' : 'auto',
        'user-select': 'none',
        position: 'relative',
      };
    },
    widgetStyle() {
      return {
        cursor: this.isEditMode ? 'move' : 'default',
        'pointer-events': this.preventWidgetEvent || this.spaceDown ? 'none' : 'inherit',
      };
    },
    eventTargetStyle() {
      return {
        width: '100%',
        height: '100%',
        overflow: this.isFullScale ? 'hidden' : '',
      };
    },
    /**
     * 대시보드 레이아웃 스타일
     * @author KangBonghun(toto1323)
     * @return {[type]} [description]
     */
    dashboardLayoutStyle() {
      return {
        transform: this.isFullScale ? `scale(${this.dashboardScaleX}, ${this.dashboardScaleY})` : `scale(${this.dashboardScale})`,
        width: `${this.dashboardWidth}px`,
        height: `${this.dashboardHeight}px`,
        background: `${this.backgroundStyle}`,
      };
    },
    backgroundStyle() {
      let style = 'none';

      if (this.backgroundType === DashboardConst.BACKGROUND_TYPE.COLOR) {
        style = this.dashboardData.bgColor;
      } else if (this.backgroundType === DashboardConst.BACKGROUND_TYPE.IMAGE) {
        style = `url(${this.$store.getters.getImageUrlById(this.dashboardData.customimageId)})`;
      }

      return style;
    },
    backgroundType() {
      return this.dashboardData ? this.dashboardData.bgType : DashboardConst.BACKGROUND_TYPE.COLOR;
    },
    /**
     * 대시보드 너비 반환
     * @author KangBonghun(toto1323)
     * @return {[type]} [description]
     */
    dashboardWidth() {
      return this.dashboardData.width;
    },
    /**
     * 대시보드 높이 반환
     * @author KangBonghun(toto1323)
     * @return {[type]} [description]
     */
    dashboardHeight() {
      return this.dashboardData.height;
    },
  },
  beforeCreate() {
    this.$store.commit('setDashboardType', DashboardConst.DASHBOARD_TYPE.COORD);
  },
  created() {
    // 위젯 트리의 DargStart, DragEnd 이벤트를 받아서 화면에 그려진 위젯이 Drop 이벤트를 받지 않도록 조치한다.
    this.$dashboardEventBus.$on(DashboardConst.EVENT_BUS.WIDGET_ADD_START, this.onWidgetAddStart);
    this.$dashboardEventBus.$on(DashboardConst.EVENT_BUS.WIDGET_ADD_END, this.onWidgetAddEnd);
    this.$dashboardEventBus.$on(DashboardConst.EVENT_BUS.WIDGET_ACTIVE_CLEAR, this.clearActiveWidget);
    this.$dashboardEventBus.$on(DashboardConst.EVENT_BUS.DASHBORD_IMAGE_DOWNLOAD, this.dashboardImageDownload);

    editHistory.apply(this);
    glowKeyframe.create();
  },
  mounted() {
    this.$el.addEventListener('wheel', this.onWheel);

    this.$el.addEventListener('mouseup', this.onMouseUpDown);
    this.$el.addEventListener('mousedown', this.onMouseUpDown);

    this.$el.addEventListener('mousemove', this.onMouseMove);

    this.$refs.dashboardLayout.$el.addEventListener('mousedown', this.onLayoutClick);

    document.body.addEventListener('keyup', this.onKeyUpDown);
    document.body.addEventListener('keydown', this.onKeyUpDown);

    window.addEventListener('resize', this.onWindowResize);

    // 대시보드 로드하기전에 자동으로 전체화면 할지 확인
    this.autoDashboardFullScreen();
  },
  beforeDestroy() {
    this.$el.removeEventListener('wheel', this.onWheel);

    this.$el.removeEventListener('mouseup', this.onMouseUpDown);
    this.$el.removeEventListener('mousedown', this.onMouseUpDown);

    this.$el.removeEventListener('mousemove', this.onMouseMove);

    this.$refs.dashboardLayout.$el.removeEventListener('mousedown', this.onLayoutClick);

    document.body.removeEventListener('keyup', this.onKeyUpDown);
    document.body.removeEventListener('keydown', this.onKeyUpDown);

    window.removeEventListener('resize', this.onWindowResize);

    this.$dashboardEventBus.$off(DashboardConst.EVENT_BUS.WIDGET_ADD_START, this.onWidgetAddStart);
    this.$dashboardEventBus.$off(DashboardConst.EVENT_BUS.WIDGET_ADD_END, this.onWidgetAddEnd);
    this.$dashboardEventBus.$off(DashboardConst.EVENT_BUS.WIDGET_ACTIVE_CLEAR, this.clearActiveWidget);
    this.$dashboardEventBus.$off(DashboardConst.EVENT_BUS.DASHBORD_IMAGE_DOWNLOAD, this.dashboardImageDownload);

    editHistory.clear();
    glowKeyframe.destroy();
  },
  methods: {
    /**
     * 위젯 데이터 새로고침
     * 위젯 개별적으로 로드하지않고, 대시보드가 일괄적으로 조회한다.
     * (위젯 개별로 로드하면 Request Pending이 발생할 확률이 높고, Data Binding되는 시점이 재각각이라서 애니메이션 효과도 통일성있게 보여지지 않는다.)
     *
     * @author KangBonghun(toto1323)
     * @return {[type]} [description]
     */
    widgetLoadData() {
      if (this.widgetLoadDataSource) {
        // 이전에 요청했는데 응답받지 않은게 있다면 취소한다.
        this.widgetLoadDataSource.cancel();
      }
      if (!isEmpty2(window.widgetInsts)) {
        // 편집모드이면 샘플데이터 로드
        if (this.isEditMode) {
          forEach(window.widgetInsts, (widget) => {
            widget.loadSampleData();
          });
        } else if (this.isMonitoringMode) {
          // 조회 파라미터 획득
          const paramList = [];
          forEach(window.widgetInsts, (widget) => {
            // 화면에 보여지고 있는 위젯만
            if (widget.visible) {
              const param = widget.getParameter();

              // 위젯의 loadData를 직접 실행시켜준다.
              if (param === null) {
                widget.loadData();
              } else {
                // 위젯으로부터 데이터 조회 Parameter를 얻는다.
                paramList.push(widget.getParameter());
              }
            }
          });

          // 위젯 새로고침 타이머 정지. 데이터 응답 후 재개시킨다. (새로고침 주기 기준을 응답 이후 시점으로 하기 위해서)
          this.removeWidgetLoadDataTask();

          // ajax 취소 시킬 수 있는 token 획득
          this.widgetLoadDataSource = axios.CancelToken.source();

          // 위젯 데이터 일괄조회
          WidgetAPI.getBundleWidgetData({
            cancelToken: this.widgetLoadDataSource.token,
            rotationNumber: this.currentRotationInfo.childNumber,
            paramList,
          }).then((response) => {
            const result = response.data;
            if (this.isMonitoringMode && result.success) {
              const dataList = result.data;
              forEach(dataList, (data) => {
                // 조회 된 데이터 Binding Event Dispatch
                Vue.prototype.$dashboardEventBus.$emit(DashboardConst.EVENT_BUS.WIDGET_BIND_DATA, data);
              });
            }
          }).catch(() => {
            // ignore
          }).finally(() => {
            this.createWidgetLoadDataTask();
          });
        }
      }
    },
    onWidgetMounted(index) {
      // 위젯에 걸려있는 마우스다운 이벤트를 제거한다. 관련 결함 : #50920
      document.documentElement.removeEventListener('mousedown', this.$refs.dragResize[index].deselect);
    },
    /**
     * ViewMode 변경되면 호출 된다.
     * @author KangBonghun(toto1323)
     * @param  {Boolean} isEditMode [description]
     * @return {[type]}             [description]
     */
    changeViewMode(isEditMode) {
      // 키보드 이벤트를 받기 위해 body에 포커싱
      document.body.focus();

      // 모든 위젯 선택 취소
      this.clearActiveWidget();

      if (isEditMode) {
        // 위젯 샘플데이터를 로드한다.
        this.widgetLoadData();

        // 좌측 영역 너비 조절
        const leftArea = document.querySelector('.drag-content.horiozontal-zone--frame-side');
        const leftAreaDrag = document.querySelector('.drag-handle.horizontal-zone--handle.horizontal');
        if (leftArea) {
          // 좌측 영역 너비 변경
          leftArea.style.width = '500px';
          leftArea.style.maxWidth = '500px';
          leftArea.style.minWidth = '500px';

          this.calcFullScale();
        }
        if (leftAreaDrag) {
          // 좌측 영역 드래그 커서 안보이도록
          leftAreaDrag.style.cursor = 'default';
        }
      }
    },
    /**
     * fullScale 상태가 변경되면 호출된다.
     * @author KangBonghun(toto1323)
     * @return {[type]} [description]
     */
    changeFullScale() {
      this.calcFullScale();
    },
    /**
     * 키 업/다운 이벤트
     * @author KangBonghun(toto1323)
     * @param  {[type]} e [description]
     * @return {[type]}   [description]
     */
    onKeyUpDown(e) {
      if (e.target !== document.body && !e.target.classList.contains('event-target')) {
        return;
      }

      if (this.isEditMode) {
        const keyCode = e.keyCode;

        // 스페이스바
        if (keyCode === 32) {
          if (e.type === 'keydown') {
            this.spaceDown = true;
          } else {
            this.spaceDown = false;
          }
        }

        // Ctrl
        if (keyCode === 17) {
          if (e.type === 'keydown') {
            this.ctrlDown = true;
          } else {
            this.ctrlDown = false;
          }
        }

        // 방향키
        if (e.type === 'keydown' && (keyCode === 37 || keyCode === 38 || keyCode === 39 || keyCode === 40)) {
          let deltaX = 0;
          let deltaY = 0;

          switch (keyCode) {
            case 37: // Left
              deltaX = -1;
              break;
            case 38: // Up
              deltaY = -1;
              break;
            case 39: // Right
              deltaX = 1;
              break;
            case 40: // Down
              deltaY = 1;
              break;
            default:
          }

          if (!e.ctrlKey) {
            deltaX *= 5;
            deltaY *= 5;
          }

          this.moveWidgets({ deltaX, deltaY });
        }

        // 단축키
        if (e.type === 'keydown' && e.ctrlKey && (keyCode === 65 || keyCode === 67 || keyCode === 86 || keyCode === 90)) {
          switch (keyCode) {
            case 65: // A
              this.allActiveWidget();
              break;
            case 67: // C
              this.copyWidget();
              break;
            case 86: // V
              this.pasteWidget();
              break;
            case 90: // Z
              editHistory.undoEditHistory();
              break;
            default:
          }
        }

        // 8: 백스페이스, 46: DELETE
        if (keyCode === 8 || keyCode === 46) {
          this.removeWidgets();
        }

        // shift
        if (keyCode === 16) {
          if (e.type === 'keydown') {
            this.shiftDown = true;
          } else {
            this.shiftDown = false;
          }
        }

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      } else {
        this.ctrlDown = false;
        this.spaceDown = false;
        this.shiftDown = false;
      }
    },
    /**
     * 마우스 누름 상태 변경
     * @author KangBonghun(toto1323)
     * @param  {[type]} e [description]
     * @return {[type]}   [description]
     */
    onMouseUpDown(e) {
      // 마우스를 눌러서 화면 이동하기 위해 사용되는 변수. (크로스브라우징 해결책)
      window.prevX = e.screenX;
      window.prevY = e.screenY;

      document.activeElement.blur();

      if (e.type === 'mousedown' && e.target.classList.contains('event-target')) {
        this.mouseDown = true;

        if (e.ctrlKey) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
        }
      } else {
        this.mouseDown = false;
        this.mouseMove = false;
      }
    },
    /**
     * 마우스 누름 상태일 때 화면 스크롤 이동
     * @author KangBonghun(toto1323)
     * @param  {[type]} e [description]
     * @return {[type]}   [description]
     */
    onMouseMove(e) {
      if (this.mouseDown) {
        // 편집 모드에서는 '이동모드'이거나 '기본편집모드'이고 '스페이스바'를 눌렀을 때만 이동한다.
        if (this.isEditMode && this.editSelectMode !== DashboardConst.EDIT_SELECT_MODE.MOVE && !this.spaceDown) {
          return;
        }

        this.mouseMove = true;

        this.$el.scrollTop = this.$el.scrollTop - (window.prevY ? e.screenY - window.prevY : 0);
        this.$el.scrollLeft = this.$el.scrollLeft - (window.prevX ? e.screenX - window.prevX : 0);

        window.prevX = e.screenX;
        window.prevY = e.screenY;
      }
    },
    /**
     * 화면 확대/축소
     * @author KangBonghun(toto1323)
     * @param  {[type]} e [description]
     * @return {[type]}   [description]
     */
    onWheel(e) {
      if (!this.isFullScale) { // 스케일 맞춤 상태가 아닌 경우에만 확대/축소한다.
        const { SCALE_STEP, MAX_SCALE, MIN_SCALE } = DashboardConst.COORD_LAYOUT_OPTION;

        // 휠 업
        if (e.deltaY < 0) {
          if (this.dashboardScale + SCALE_STEP > MAX_SCALE) {
            this.dashboardScale = MAX_SCALE;
          } else {
            const scale = Math.round((this.dashboardScale + SCALE_STEP) * 100) / 100;
            this.dashboardScale = scale;
          }
        }

        // 휠 다운
        if (e.deltaY > 0) {
          if (this.dashboardScale - SCALE_STEP < MIN_SCALE) {
            this.dashboardScale = MIN_SCALE;
          } else {
            const scale = Math.round((this.dashboardScale - SCALE_STEP) * 100) / 100;
            this.dashboardScale = scale;
          }
        }

        // 스케일 변경 이벤트 디스패치
        this.$dashboardEventBus.$on(DashboardConst.EVENT_BUS.DASHBOARD_SCALE_CHANGE, this.dashboardScale);
      }

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    },
    /**
     * 창 리사이즈되면 호출된다.
     * @author KangBonghun(toto1323)
     * @return {[type]} [description]
     */
    onWindowResize() {
      this.calcFullScale();
    },
    /**
     * 스케일 맞춤일경우 비율을 계산한다.
     * @author KangBonghun(toto1323)
     * @return {[type]} [description]
     */
    calcFullScale() {
      if (this.isFullScale && this.$el) {
        this.previousScrollPosition.top = this.$el.scrollTop;
        this.previousScrollPosition.left = this.$el.scrollLeft;
        // 51210 일감 관련. IE에서는 scale을 변경 시 자동으로 위치변환(wrapper 영역에 맞게)을 지원하지 않는다.
        // 이를 해결하기 위해 none으로 숨긴 뒤 nextTick에 다시 block으로 변경하여 위치를 재계산하는 방법을 사용.
        this.$refs.dashboardLayout.$el.style.display = 'none';
        this.$nextTick(() => {
          this.dashboardScaleX = this.$el.clientWidth / this.dashboardWidth;
          this.dashboardScaleY = this.$el.clientHeight / this.dashboardHeight;
          this.$refs.dashboardLayout.$el.style.display = 'block';
        });
      } else if (!this.isFullScale && this.$el) {
        this.$nextTick(() => {
          this.$el.scrollTop = this.previousScrollPosition.top || 0;
          this.$el.scrollLeft = this.previousScrollPosition.left || 0;
        });
      }
    },
    /**
     * 대시보드 로드 완료 후 호출되는 콜백
     * @author KangBonghun(toto1323)
     * @return {[type]} [description]
     */
    loadDashboardComplete() {
      this.calcFullScale();

      editHistory.clearEditHistoryStack();

      this.widgetLoadData();
    },
    /**
     * 위켓을 클릭하면 호출된다.
     * @author KangBonghun(toto1323)
     * @param  {[type]} e        [description]
     * @param  {[type]} widgetId [description]
     * @return {[type]}          [description]
     */
    onWidgetMouseDown(e, widgetId) {
      if (this.isEditMode) {
        this.draggingWidget = {};

        const clickWidget = find(this.widgetLayout, ['i', widgetId]);
        if (this.ctrlDown) {
          if (clickWidget.active) {
            this.deActiveWidget(widgetId);
          } else {
            this.addActiveWidget(widgetId);
          }
        } else if (!clickWidget.active) {
          this.activeWidget(widgetId);
        }

        this.$nextTick(() => {
          this.$refs.dragResize.forEach((d) => {
            if (d.active) {
              // 위젯을 드래그하여 위치를 이동시키면 선택 된 다른 위젯들도 함께 위치가 변경되도록하는 조치
              d.bodyDown(e);
            }
          });
        });
      }
    },
    onWidgetMouseUp() {
      if (this.isEditMode) {
        // 위치 이동 된 위젯이 존재한다면
        if (!isEmpty(this.draggingWidget)) {
          const historyElements = cloneDeep(filter(this.widgetLayout, (widget) => (has(this.draggingWidget, widget.i))));

          editHistory.addEditHistory({
            type: DashboardConst.HISTORY_TYPE.LOCATION,
            elements: historyElements,
          });
        }

        this.draggingWidget = {};
      }
    },
    /**
     * 레이아웃 영역을 클릭하면 호출된다.
     * @author KangBonghun(toto1323)
     * @return {[type]} [description]
     */
    onLayoutClick() {
      this.clearActiveWidget();
    },
    onWidgetDragging(newRect, widgetId) {
      this.draggingWidget[widgetId] = widgetId;
    },
    /**
     * 위젯을 드래그하면 호출된다.
     * @author KangBonghun(toto1323)
     * @param  {[type]} newRect  [description]
     * @param  {[type]} widgetId [description]
     * @return {[type]}          [description]
     */
    onWidgetDragged(newRect, widgetId) {
      this.dragWidget({
        widgetId,
        x: newRect.left,
        y: newRect.top,
      });
    },
    /**
     * 위젯을 리사이즈하면 호출된다.
     * @author KangBonghun(toto1323)
     * @param  {[type]} newRect  [description]
     * @param  {[type]} widgetId [description]
     * @return {[type]}          [description]
     */
    onWidgetResized(newRect, widgetId) {
      this.resizeWidget({
        widgetId,
        x: newRect.left,
        y: newRect.top,
        w: newRect.width,
        h: newRect.height,
      });
    },
    /**
     * 위젯을 선택상태로 변경한다. (선택한 위젯 외 다른 위젯은 미선택 상태로 변경)
     * @author KangBonghun(toto1323)
     * @param  {[type]} widgetId [description]
     * @return {[type]}          [description]
     */
    activeWidget(widgetId) {
      this.clearActiveWidget();
      this.$nextTick(() => {
        this.addActiveWidget(widgetId);
      });
    },
    /**
     * 위젯을 추가로 선택상태로 변경한다.
     * @author KangBonghun(toto1323)
     * @param  {[type]} widgetId [description]
     */
    addActiveWidget(widgetId) {
      const widget = find(this.widgetLayout, ['i', widgetId]);
      if (!isEmpty(widget)) {
        Vue.set(widget, 'active', true);

        this.$dashboardEventBus.$emit(DashboardConst.EVENT_BUS.WIDGET_ACTIVE_CHANGE);
      }
    },
    /**
     * 모든 위젯을 선택상태로 변경한다.
     * @author KangBonghun(toto1323)
     * @return {[type]} [description]
     */
    allActiveWidget() {
      if (!isEmpty(this.widgetLayout)) {
        forEach(this.widgetLayout, (widget) => {
          if (this.widgetVisible(widget)) {
            Vue.set(widget, 'active', true);
          }
        });

        this.$dashboardEventBus.$emit(DashboardConst.EVENT_BUS.WIDGET_ACTIVE_CHANGE);
      }
    },
    /**
     * 모든 위젯을 미선택 상태로 변경한다.
     * @author KangBonghun(toto1323)
     * @return {[type]} [description]
     */
    clearActiveWidget() {
      const deactiveTargets = filter(this.widgetLayout, ['active', true]);

      if (!isEmpty(deactiveTargets)) {
        forEach(deactiveTargets, (widget) => {
          Vue.set(widget, 'active', false);
        });

        this.$dashboardEventBus.$emit(DashboardConst.EVENT_BUS.WIDGET_ACTIVE_CHANGE);
      }
    },
    /**
     * 위젯을 미선택 상태로 변경한다.
     * @author KangBonghun(toto1323)
     * @param  {[type]} widgetId [description]
     * @return {[type]}          [description]
     */
    deActiveWidget(widgetId) {
      const widget = find(this.widgetLayout, ['i', widgetId]);
      if (!isEmpty(widget)) {
        Vue.set(widget, 'active', false);

        this.$dashboardEventBus.$emit(DashboardConst.EVENT_BUS.WIDGET_ACTIVE_CHANGE);
      }
    },
    /**
     * 위젯의 위치를 이동시킨다.
     * @author KangBonghun(toto1323)
     * @param  {[type]} info [description]
     * @return {[type]}      [description]
     */
    moveWidgets(info) {
      const activeWidgets = filter(this.widgetLayout, ['active', true]);
      forEach(activeWidgets, (widget) => {
        Vue.set(widget, 'x', (widget.x + info.deltaX));
        Vue.set(widget, 'y', (widget.y + info.deltaY));
      });

      const historyElements = cloneDeep(activeWidgets);
      editHistory.addEditHistory({
        type: DashboardConst.HISTORY_TYPE.LOCATION,
        elements: historyElements,
      });
    },
    /**
     * 위젯을 드래깅하면 호출된다.
     * @author KangBonghun(toto1323)
     * @param  {[type]} param [description]
     * @return {[type]}       [description]
     */
    dragWidget(param) {
      const widget = find(this.widgetLayout, ['i', param.widgetId]);

      Vue.set(widget, 'x', isEmpty(param.x) ? widget.x : param.x);
      Vue.set(widget, 'y', isEmpty(param.y) ? widget.y : param.y);
    },
    /**
     * 위젯을 리사이징하면 호출된다.
     * @author KangBonghun(toto1323)
     * @param  {[type]} param [description]
     * @return {[type]}       [description]
     */
    resizeWidget(param) {
      const historyElements = [];
      const widget = find(this.widgetLayout, ['i', param.widgetId]);

      historyElements.push(cloneDeep(widget));

      Vue.set(widget, 'x', param.x);
      Vue.set(widget, 'y', param.y);
      Vue.set(widget, 'w', param.w);
      Vue.set(widget, 'h', param.h);

      this.$dashboardEventBus.$emit(DashboardConst.EVENT_BUS.WIDGET_RESIZE, {
        widgetId: param.widgetId,
        newH: param.h,
        newW: param.w,
      });

      editHistory.addEditHistory({
        type: DashboardConst.HISTORY_TYPE.SIZE,
        elements: historyElements,
      });
    },
    /**
     * 오브젝트를 레이아웃에 Drop하면 호출되며, 오브젝트를 화면에 추가한다.
     * @author KangBonghun(toto1323)
     * @param  {[type]} node [description]
     * @param  {[type]} evt  [description]
     * @return {[type]}      [description]
     */
    onDropNode(node, evt) {
      const dropX = evt.offsetX;
      const dropY = evt.offsetY;

      let newWidget;
      if (has(node, 'widgetTemplId')) { // 위젯
        newWidget = this.onDropWidget(node, dropX, dropY);
      } else if (has(node, 'customimageId')) { // 이미지
        newWidget = this.onDropImage(node, dropX, dropY);
      } else if (node.data && node.data.obj) { // 관리대상
        newWidget = this.onDropManaged(node, dropX, dropY);
      }

      if (!isEmpty(newWidget)) {
        this.$nextTick(() => {
          // 선택 상태로 변경
          this.activeWidget(newWidget.i);

          // 위젯을 Drop한 좌표 가운데로 위치 조정
          this.dragWidget({
            widgetId: newWidget.i,
            x: dropX - (this.getWidgetDefaultSize(newWidget.widgetTemplId)[0] / 2),
            y: dropY - (this.getWidgetDefaultSize(newWidget.widgetTemplId)[1] / 2),
          });

          // 변경 이력 추가
          editHistory.addEditHistory({
            type: DashboardConst.HISTORY_TYPE.ADD,
            elements: [newWidget.i],
          });
        });
      }
    },
    /**
     * 위젯 오브젝트 Drop
     * @author KangBonghun(toto1323)
     * @param  {[type]} node  [description]
     * @param  {[type]} dropX [description]
     * @param  {[type]} dropY [description]
     * @return {[type]}       [description]
     */
    onDropWidget(node, dropX, dropY) {
      const newWidget = this.createWidget({
        x: dropX,
        y: dropY,
        w: this.getWidgetDefaultSize(node.widgetTemplId)[0],
        h: this.getWidgetDefaultSize(node.widgetTemplId)[1],
        widgetTemplId: node.widgetTemplId,
      });

      return newWidget;
    },
    /**
     * 이미지 오브젝트 Drop
     * @author KangBonghun(toto1323)
     * @param  {[type]} node  [description]
     * @param  {[type]} dropX [description]
     * @param  {[type]} dropY [description]
     * @return {[type]}       [description]
     */
    onDropImage(node, dropX, dropY) {
      const widgetTemplId = 'DecorationWidget';

      const newWidget = this.createWidget({
        x: dropX,
        y: dropY,
        w: this.getWidgetDefaultSize(widgetTemplId)[0],
        h: this.getWidgetDefaultSize(widgetTemplId)[1],
        widgetTemplId,
      });

      if (!isEmpty(newWidget)) {
        this.$nextTick(() => {
          const widgetInfo = cloneDeep(this.dashboardWidget[newWidget.i]);
          assign(widgetInfo.widgetInfo.viewOptions, {
            shapeType: 'image',
            imageId: node.customimageId,
          });
          this.$dashboardEventBus.$emit(DashboardConst.EVENT_BUS.WIDGET_APPLY_SET, widgetInfo, 'imageId', node.customimageId);
        });
      }

      return newWidget;
    },
    /**
     * 관리대상 오브젝트 Drop
     * @author KangBonghun(toto1323)
     * @param  {[type]} node  [description]
     * @param  {[type]} dropX [description]
     * @param  {[type]} dropY [description]
     * @return {[type]}       [description]
     */
    onDropManaged(node, dropX, dropY) {
      const widgetTemplId = 'ManagedObjectWidget';

      const newWidget = this.createWidget({
        x: dropX,
        y: dropY,
        w: this.getWidgetDefaultSize(widgetTemplId)[0],
        h: this.getWidgetDefaultSize(widgetTemplId)[1],
        widgetTemplId,
      });

      if (!isEmpty(newWidget)) {
        this.$nextTick(() => {
          const obj = node.data.obj;
          const widgetInfo = cloneDeep(this.dashboardWidget[newWidget.i]);
          assign(widgetInfo.widgetInfo.dataOptions, {
            managetypeId: obj.objManagetypeId,
            managetypeKey: '_',
            obj,
            objIds: [obj.objId],
            objTreeKeys: [node.treeKey],
            objLabel: node.label,
          });
          this.$dashboardEventBus.$emit(DashboardConst.EVENT_BUS.WIDGET_APPLY_SET, widgetInfo);
        });
      }

      return newWidget;
    },
    /**
     * 그려져도 되는 위젯인지 체크한다.
     * @author KangBonghun(toto1323)
     * @param  {[type]} widget [description]
     * @return {[type]}      [description]
     */
    validateCreateWidget(widget) {
      if (widget.widgetTemplId === 'SlideWidget') {
        if (find(this.widgetLayout, (layout) => layout.widgetTemplId === 'SlideWidget' && layout.i !== DashboardConst.TEMP_WIDGET_ID)) {
          this.$message(msgUtil.getCustomMsgData('슬라이드 위젯은 2개 이상 구성할 수 없습니다.'));
          return false;
        }
      }
      return true;
    },
    /**
     * 위젯을 생성한다.
     * @author KangBonghun(toto1323)
     * @param  {[type]} newWidget [description]
     */
    createWidget(newWidget) {
      if (!this.validateCreateWidget(newWidget)) {
        return null;
      }

      const lastWidget = maxBy(this.widgetLayout, 'i');
      const newWidgetId = lastWidget ? Number(lastWidget.i) + 1 : 0;
      const newZIndex = this.getMaxZIndex();

      // 새로운 위젯 아이디 생성
      newWidget.i = newWidgetId;
      newWidget.z = newZIndex + 1;

      this.$store.commit('addWidget', newWidget);

      return newWidget;
    },
    /**
     * 선택 된 위젯을 삭제한다.
     * @author KangBonghun(toto1323)
     * @return {[type]} [description]
     */
    removeWidgets() {
      const historyElements = [];
      const activeWidgets = filter(this.widgetLayout, ['active', true]);

      forEach(activeWidgets, (widget) => {
        historyElements.push({
          layout: cloneDeep(widget),
          widget: cloneDeep(this.dashboardWidget[widget.i]),
        });
        this.$store.commit('removeWidget', widget.i);
      });

      this.$dashboardEventBus.$emit(DashboardConst.EVENT_BUS.WIDGET_ACTIVE_CHANGE);

      if (!isEmpty(historyElements)) {
        // 변경 이력 추가
        editHistory.addEditHistory({
          type: DashboardConst.HISTORY_TYPE.REMOVE,
          elements: historyElements,
        });
      }
    },
    /**
     * 선택 된 위젯을 복사한다.
     * @author KangBonghun(toto1323)
     * @return {[type]} [description]
     */
    copyWidget() {
      this.copiedWidgets = map(filter(this.widgetLayout, ['active', true]), (layout) => ({
        layout: cloneDeep(layout),
        widget: cloneDeep(this.dashboardWidget[layout.i]),
      }));
    },
    /**
     * 복사 한 위젯을 붙여넣기한다.
     * @author KangBonghun(toto1323)
     * @return {[type]} [description]
     */
    pasteWidget() {
      if (!isEmpty(this.copiedWidgets)) {
        const newWidgets = [];

        forEach(this.copiedWidgets, (copiedWidget) => {
          // 위젯 추가
          const widgetLayout = copiedWidget.layout;
          const newWidgetLayout = this.createWidget(widgetLayout);

          if (newWidgetLayout === null) {
            return;
          }

          newWidgets.push(newWidgetLayout);

          this.$nextTick(() => {
            // 위젯 속성 복사
            const widgetInfo = copiedWidget.widget;
            widgetInfo.dashboardId = this.currentDashboardId;
            widgetInfo.widgetId = newWidgetLayout.i;
            // this.$store.commit('updateWidget', widgetInfo);
            this.$dashboardEventBus.$emit(DashboardConst.EVENT_BUS.WIDGET_APPLY_SET, widgetInfo);
          });
        });

        // 붙여넣기 한 오브젝트를 선택한 상태로 변경하고 복사한다.
        this.$nextTick(() => {
          this.clearActiveWidget();
          forEach(newWidgets, (widget) => {
            this.addActiveWidget(widget.i);

            // 위젯 위치 재조정 (대시보드 영역 밖으로 넘어가는것 방지)
            this.dragWidget({
              widgetId: widget.i,
              x: widget.x + 50,
              y: widget.y + 50,
            });
          });
          this.copyWidget();

          // 변경 이력 추가
          editHistory.addEditHistory({
            type: DashboardConst.HISTORY_TYPE.ADD,
            elements: map(newWidgets, (widget) => (widget.i)),
          });
        });
      }
    },
    /**
     * 현재 그려진 위젯에서 z 최대값을 반환한다.
     * @author KangBonghun(toto1323)
     * @return {Number} z 최대값
     */
    getMaxZIndex() {
      const maxZ = maxBy(this.widgetLayout, 'z');
      return isNaN(maxZ && maxZ.z) ? 1 : maxZ.z;
    },
    /**
     * 새로운 위젯을 Drop하는 위치에 기존에 그린 위젯이 있을 때 기존 위젯의 이벤트를 막지 않으면 엉뚱한 위치에 새로운 위젯이 그려진다.
     * 따라서, 새로운 위젯을 그릴려고 Drag를 시작하면 기존 위젯의 이벤트를 잠시 막아두고, Drop 후에는 이벤트를 풀어준다.
     * @author KangBonghun(toto1323)
     * @return {[type]} [description]
     */
    onWidgetAddStart() {
      this.preventWidgetEvent = true;
    },
    /**
     * 새로운 위젯을 Drop하는 위치에 기존에 그린 위젯이 있을 때 기존 위젯의 이벤트를 막지 않으면 엉뚱한 위치에 새로운 위젯이 그려진다.
     * 따라서, 새로운 위젯을 그릴려고 Drag를 시작하면 기존 위젯의 이벤트를 잠시 막아두고, Drop 후에는 이벤트를 풀어준다.
     * @author KangBonghun(toto1323)
     * @return {[type]} [description]
     */
    onWidgetAddEnd() {
      this.preventWidgetEvent = false;
    },
    /**
     * 대시보드 이미지 다운로드
     * @author KangBonghun(toto1323)
     * @return {[type]} [description]
     */
    dashboardImageDownload() {
      // 대시보드를 화면에 표현된 그대로 보여주기 위해 css의 transforms 속성 사용 안하도록 변경
      this.$nextTick(() => {
        const dashboard = this.$refs.dashboardLayout;
        const el = dashboard.$el;
        const fileName = this.$store.getters.getDashboardData.dashboardName;

        // 대시보드레이아웃 dom의 el을 html2canvas에 넘겨 해당 부분 이미지 화 하도록 설정
        html2canvas(el, {
          logging: false,
          async: false,
          backgroundColor: '#F4F4F5',
        }).then((canvas) => {
          const dataURL = canvas.toDataURL('image/png').replace('data:image/png', 'data:application/octet-stream');
          const link = document.createElement('a');
          link.href = dataURL;
          link.download = `${fileName}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }).catch((error) => {
          console.log(error);
        });
      });
    },
    getWidgetDefaultSize(widgetTemplId) {
      const w = (DashboardConst.WIDGET_DEFAULT_SIZE[widgetTemplId] && DashboardConst.WIDGET_DEFAULT_SIZE[widgetTemplId][0]) || DashboardConst.COORD_LAYOUT_OPTION.DEFAULT_WIDGET_W;
      const h = (DashboardConst.WIDGET_DEFAULT_SIZE[widgetTemplId] && DashboardConst.WIDGET_DEFAULT_SIZE[widgetTemplId][1]) || DashboardConst.COORD_LAYOUT_OPTION.DEFAULT_WIDGET_H;

      return [w, h];
    },
  },
};
</script>

<style module>
.dashboard-layout {
  transform-origin: 0 0;
  background-size: 100% 100% !important;
}
.empty-dashboard {
  width: 100%;
  height: 100%;
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>



// WEBPACK FOOTER //
// src/views/dashboard/components/frame/CoordDashboardLayout.vue